"use client"

import { PDPSeo } from "components/Shop/PDPPage/PDPSeo"
import type { PAGE_EXPERIMENT_PDP_WITH_OPEN_GATE_UI } from "data/experiment"
import { withTestData } from "data/pdp/experiment"
import { useVariantIds } from "hooks/useVariantIds"

import type {
  MembershipOptionsV2,
  PricingToggle,
  ShippingType,
  VariantType,
} from "types"
import { PDPPage } from "./PDPPage"
import { GiftMembershipPDP } from "./PDPPage/GiftMembershipPDP"
import { getTrackingEventId } from "lib/api/utils/trackingEventId"
import { useMemo } from "react"

type ExperimentKeys = keyof typeof PAGE_EXPERIMENT_PDP_WITH_OPEN_GATE_UI.values
type ExperimentValues =
  (typeof PAGE_EXPERIMENT_PDP_WITH_OPEN_GATE_UI.values)[ExperimentKeys]
type PDPProps = {
  variant: VariantType
  preview: boolean
  giftMembershipProductId
  shippingInfo?: ShippingType[]
  useCloudinary?: boolean
  pricingToggle?: PricingToggle
  openGate: {
    isOpenGate: boolean
    openGateVariant: ExperimentValues
  }
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}

const PDP = ({
  variant,
  preview,
  giftMembershipProductId,
  shippingInfo,
  useCloudinary = false,
  pricingToggle,
  openGate,
  pdpMembershipOptions,
  membershipInBag,
}: PDPProps): JSX.Element => {
  const eventId = useMemo(() => getTrackingEventId(), [])

  if (variant.id === giftMembershipProductId) {
    return (
      <GiftMembershipPDP
        variant={variant}
        preview={preview}
        eventId={eventId}
        shippingInfo={shippingInfo}
      />
    )
  }

  return (
    <PDPPage
      variant={withTestData(variant)}
      preview={preview}
      shippingInfo={shippingInfo}
      useCloudinary={useCloudinary}
      pricingToggle={pricingToggle}
      isOpenGate={openGate.isOpenGate}
      eventId={eventId}
      pdpMembershipOptions={pdpMembershipOptions}
      membershipInBag={membershipInBag}
    />
  )
}

export const PDPWrapper: React.FC<{
  variant: VariantType
  preview: boolean
  urlRef: string | null
  shippingInfo?: ShippingType[]
  useCloudinary?: boolean
  pricingToggle?: PricingToggle
  openGate: {
    isOpenGate: boolean
    openGateVariant: ExperimentValues
  }
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}> = ({
  variant,
  preview,
  urlRef,
  shippingInfo,
  pricingToggle,
  useCloudinary,
  openGate,
  pdpMembershipOptions,
  membershipInBag,
}) => {
  const { giftMembershipProductId } = useVariantIds()

  return (
    <>
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
      />
      <PDPSeo variant={variant} urlRef={urlRef} />
      <PDP
        // TODO: add a test for this 👋
        openGate={openGate}
        variant={variant}
        preview={preview}
        giftMembershipProductId={giftMembershipProductId}
        shippingInfo={shippingInfo}
        useCloudinary={useCloudinary}
        pricingToggle={pricingToggle}
        pdpMembershipOptions={pdpMembershipOptions}
        membershipInBag={membershipInBag}
      />
    </>
  )
}
